import React, { useState, useEffect } from 'react';
import {
  X,
  Package,
  PlusCircle,
  AlertTriangle,
  LogOut,
  Search,
  Trash2,
  ShieldCheck,
  FolderPlus,
  ExternalLink,
  ClipboardPaste
} from 'lucide-react';
import AuthForm from './AuthForm';
import ProductForm from './ProductForm';
import CategoryForm from './CategoryForm';
import GreyCardsTab from './GreyCardsTab';
import { formatVND } from '../../utils/formatters';

export default function AdminModal({
  isOpen,
  onClose,
  auth,
  products = [],
  categories = [],
  onAddProduct,
  onDeleteProduct,
  onPurgeGreyCards,
  onDeleteSingleGreyCard,
  onReloadCategories,
  scanner,
  initialTab = 'add',
  prefillUrl = ''
}) {
  const [activeTab, setActiveTab] = useState(initialTab || 'add'); // 'add' | 'all' | 'category' | 'grey'
  const [prodSearch, setProdSearch] = useState('');
  const [currentPrefill, setCurrentPrefill] = useState(prefillUrl || '');

  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
    if (isOpen && prefillUrl) {
      setCurrentPrefill(prefillUrl);
    }
  }, [isOpen, initialTab, prefillUrl]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const greyList = products.filter(p => p.is_grey === 1);

  const filteredAdminProducts = products.filter(p => {
    if (!prodSearch.trim()) return true;
    const q = prodSearch.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      (p.category_name && p.category_name.toLowerCase().includes(q)) ||
      (p.platform && p.platform.toLowerCase().includes(q))
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-fade-in select-none">
      <div className="w-full max-w-4xl max-h-[90vh] bg-[#220407] border border-[#5A0D14] rounded-2xl shadow-2xl flex flex-col overflow-hidden text-[#FFF5E6]">
        {/* Modal Window Header */}
        <div className="h-14 bg-[#2D060B] border-b border-[#4D0A11] px-4 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] flex items-center justify-center text-xs font-bold">
              <ShieldCheck size={14} className="text-[#240306]" />
            </span>
            <span className="text-sm font-bold text-[#FFE29A]">
              Quản trị viên — Lại AI
            </span>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-amber-200/60 hover:text-[#FFF5E6] hover:bg-[#3D080E] transition"
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* Not Authenticated State */}
        {!auth.isAdmin ? (
          <div className="p-6 overflow-y-auto max-w-md mx-auto w-full my-auto">
            <div className="text-center mb-6">
              <h3 className="text-base font-bold text-[#FFE29A] mb-1">
                Đăng nhập Quản trị viên
              </h3>
              <p className="text-xs text-amber-200/60">
                Nhập email được ủy quyền để quản lý sản phẩm và thẻ hỏng.
              </p>
            </div>
            <AuthForm auth={auth} />
          </div>
        ) : (
          /* Authenticated Dashboard - Left Side Tabs Navigation */
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
            {/* Left Side: Navigation Tabs / Tags */}
            <div className="w-full md:w-64 bg-[#260408] border-b md:border-b-0 md:border-r border-[#4D0A11] p-3.5 flex flex-col justify-between shrink-0">
              <div className="space-y-3">
                {/* Top Priority Action Button: Dán từ Clipboard & Thêm ngay */}
                <div>
                  <button
                    onClick={async () => {
                      setActiveTab('add');
                      try {
                        if (navigator.clipboard && navigator.clipboard.readText) {
                          const text = await navigator.clipboard.readText();
                          const m = text?.trim().match(/https?:\/\/[^\s]+/i);
                          const url = m ? m[0] : text?.trim();
                          if (url) {
                            setCurrentPrefill(url);
                            return;
                          }
                        }
                      } catch (err) {
                        console.warn('Clipboard read failed:', err);
                      }
                    }}
                    className={`w-full py-2.5 px-3 rounded-xl font-black text-xs flex items-center justify-center gap-2 transition shadow-md ${
                      activeTab === 'add'
                        ? 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] ring-2 ring-[#FFE29A]'
                        : 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] hover:brightness-110'
                    }`}
                    title="Dán link từ bộ nhớ tạm và tự động chạy"
                  >
                    <ClipboardPaste size={15} />
                    <span>📋 Dán Clipboard & Thêm ngay</span>
                  </button>
                </div>

                <div className="text-[10px] font-bold text-amber-200/50 uppercase tracking-wider px-1">
                  Danh mục quản trị
                </div>

                {/* Vertical Tabs List on the Left Side */}
                <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-1 md:pb-0">
                  <button
                    onClick={() => setActiveTab('add')}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition text-left ${
                      activeTab === 'add'
                        ? 'bg-[#40080F] text-[#FFE29A] border border-[#7A131E] font-bold shadow-xs'
                        : 'text-amber-200/70 hover:bg-[#34070B] hover:text-[#FFF5E6]'
                    }`}
                  >
                    <PlusCircle size={15} className="text-[#F5A623] shrink-0" />
                    <span className="truncate">Thêm link mới</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('all')}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition text-left ${
                      activeTab === 'all'
                        ? 'bg-[#40080F] text-[#FFE29A] border border-[#7A131E] font-bold shadow-xs'
                        : 'text-amber-200/70 hover:bg-[#34070B] hover:text-[#FFF5E6]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5 truncate">
                      <Package size={15} className="text-amber-300 shrink-0" />
                      <span className="truncate">Kho sản phẩm</span>
                    </span>
                    <span className="text-[10px] font-bold bg-[#34070B] px-2 py-0.5 rounded-full text-amber-200/80">
                      {products.length}
                    </span>
                  </button>

                  <button
                    onClick={() => setActiveTab('category')}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition text-left ${
                      activeTab === 'category'
                        ? 'bg-[#40080F] text-[#FFE29A] border border-[#7A131E] font-bold shadow-xs'
                        : 'text-amber-200/70 hover:bg-[#34070B] hover:text-[#FFF5E6]'
                    }`}
                  >
                    <FolderPlus size={15} className="text-amber-300 shrink-0" />
                    <span className="truncate">Thêm danh mục</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('grey')}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition text-left ${
                      activeTab === 'grey'
                        ? 'bg-rose-950/80 text-rose-200 border border-rose-700/60 font-bold shadow-xs'
                        : greyList.length > 0
                        ? 'text-rose-300 bg-rose-950/30 hover:bg-rose-900/40'
                        : 'text-amber-200/70 hover:bg-[#34070B] hover:text-[#FFF5E6]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5 truncate">
                      <AlertTriangle size={15} className="text-rose-400 shrink-0" />
                      <span className="truncate">Thẻ hỏng / Hết hàng</span>
                    </span>
                    <span className="text-[10px] font-bold bg-rose-950 px-2 py-0.5 rounded-full text-rose-300">
                      {greyList.length}
                    </span>
                  </button>
                </nav>
              </div>

              {/* Admin Session info & Logout button */}
              <div className="pt-3 mt-3 border-t border-[#4D0A11] flex md:flex-col gap-2 justify-between items-center md:items-stretch">
                <div className="text-[11px] text-amber-200/50 truncate">
                  <span className="block truncate font-mono text-[10px] text-amber-200/70">
                    {auth.adminEmail || 'ducnhan762013@gmail.com'}
                  </span>
                  <span className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    Đang trực tuyến
                  </span>
                </div>
                <button
                  onClick={auth.logout}
                  className="w-full py-1.5 px-2.5 rounded-lg text-amber-200/60 hover:text-rose-300 hover:bg-rose-950/40 border border-transparent hover:border-rose-900/50 transition text-xs font-semibold flex items-center justify-center gap-1.5"
                >
                  <LogOut size={13} />
                  <span>Đăng xuất</span>
                </button>
              </div>
            </div>

            {/* Right Side: Tab Contents */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#1C0204]">
              {/* Tab 1: All Products */}
              {activeTab === 'all' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-200/50" />
                      <input
                        type="text"
                        value={prodSearch}
                        onChange={(e) => setProdSearch(e.target.value)}
                        placeholder="Tìm trong kho hàng..."
                        className="w-full bg-[#2B0509] border border-[#520B12] rounded-xl pl-9 pr-3 py-1.5 text-xs text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none focus:border-[#F5A623]"
                      />
                    </div>
                    <button
                      onClick={() => setActiveTab('add')}
                      className="px-3 py-1.5 bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm hover:brightness-110 transition"
                    >
                      <PlusCircle size={13} />
                      <span>Thêm link mới</span>
                    </button>
                  </div>

                  <div className="border border-[#4A0A10] rounded-xl overflow-hidden divide-y divide-[#3A080D]">
                    {filteredAdminProducts.length === 0 ? (
                      <div className="p-8 text-center text-xs text-amber-200/50">
                        Không có sản phẩm nào.
                      </div>
                    ) : (
                      filteredAdminProducts.map(p => (
                        <div key={p.id} className="p-3 flex items-center justify-between gap-3 hover:bg-[#2B0509] transition">
                          <div className="flex items-center gap-3 min-w-0">
                            <img
                              src={p.image_url}
                              alt=""
                              referrerPolicy="no-referrer"
                              className="w-10 h-10 object-cover rounded-lg border border-[#520B12] shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-[#FFF5EB] truncate">
                                {p.title}
                              </div>
                              <div className="text-[11px] text-amber-200/60 flex items-center gap-2 mt-0.5">
                                <span className="font-bold text-[#FFE29A]">{formatVND(p.price_vnd || p.price)}</span>
                                <span>•</span>
                                <span className="uppercase font-semibold">{p.platform}</span>
                                <span>•</span>
                                <span>{p.category_name}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              href={p.affiliate_url || p.target_url}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-amber-200/60 hover:text-amber-200 rounded hover:bg-[#3A080D]"
                              title="Mở link gốc"
                            >
                              <ExternalLink size={13} />
                            </a>
                            <button
                              onClick={() => onDeleteProduct(p.id)}
                              className="p-1.5 text-amber-200/60 hover:text-rose-400 rounded hover:bg-rose-950/40 transition"
                              title="Xóa sản phẩm"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Tab 2: Add Product (Auto-Scraping) */}
              {activeTab === 'add' && (
                <div className="max-w-xl mx-auto">
                  <ProductForm
                    categories={categories}
                    onAddProduct={onAddProduct}
                    prefillUrl={currentPrefill || prefillUrl}
                    onSuccess={() => {
                      setCurrentPrefill('');
                    }}
                  />
                </div>
              )}

              {/* Tab 3: Add Category */}
              {activeTab === 'category' && (
                <div className="max-w-md mx-auto">
                  <CategoryForm
                    onSuccess={() => {
                      onReloadCategories && onReloadCategories();
                      setActiveTab('all');
                    }}
                  />
                </div>
              )}

              {/* Tab 4: Grey Cards Triage */}
              {activeTab === 'grey' && (
                <GreyCardsTab
                  greyCards={greyList}
                  onPurgeAll={onPurgeGreyCards}
                  onDeleteSingle={onDeleteSingleGreyCard}
                  scanner={scanner}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
